import { watch, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useToast } from './useToast'

export const IDLE_TIMEOUT_MS = 60 * 60 * 1000
const LAST_ACTIVITY_KEY = 'avance-last-activity'
const ACTIVITY_THROTTLE_MS = 30 * 1000

const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll', 'click']

export function useIdleLogout(timeoutMs = IDLE_TIMEOUT_MS) {
  const authStore = useAuthStore()
  const router = useRouter()
  const { warning } = useToast()

  let timeoutId = null
  let lastTouchAt = 0
  let idleLogoutInProgress = false

  const getLastActivity = () => {
    const raw = localStorage.getItem(LAST_ACTIVITY_KEY)
    const parsed = raw ? Number(raw) : NaN
    return Number.isFinite(parsed) ? parsed : null
  }

  const setLastActivity = (ts = Date.now()) => {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(ts))
  }

  const clearLastActivity = () => {
    localStorage.removeItem(LAST_ACTIVITY_KEY)
  }

  const clearTimer = () => {
    if (timeoutId) {
      clearTimeout(timeoutId)
      timeoutId = null
    }
  }

  const scheduleLogout = () => {
    clearTimer()

    const last = getLastActivity()
    if (!last) {
      setLastActivity()
      timeoutId = setTimeout(onIdle, timeoutMs)
      return
    }

    const remaining = timeoutMs - (Date.now() - last)
    if (remaining <= 0) {
      onIdle()
      return
    }

    timeoutId = setTimeout(onIdle, remaining)
  }

  const onIdle = async () => {
    if (!authStore.isAuthenticated || idleLogoutInProgress) return

    idleLogoutInProgress = true
    clearTimer()
    clearLastActivity()

    try {
      await authStore.logout()
      warning(
        'Sessão encerrada',
        'Você foi desconectado por inatividade.'
      )
      if (router.currentRoute.value?.name !== 'Login') {
        await router.replace({ name: 'Login' })
      }
    } finally {
      idleLogoutInProgress = false
    }
  }

  const onActivity = () => {
    if (!authStore.isAuthenticated || idleLogoutInProgress) return

    const now = Date.now()
    if (now - lastTouchAt < ACTIVITY_THROTTLE_MS) return
    lastTouchAt = now

    setLastActivity(now)
    scheduleLogout()
  }

  const onVisibilityChange = () => {
    if (document.visibilityState !== 'visible' || !authStore.isAuthenticated) return

    const last = getLastActivity()
    if (last && Date.now() - last >= timeoutMs) {
      onIdle()
      return
    }

    scheduleLogout()
  }

  const start = () => {
    const last = getLastActivity()
    if (last && Date.now() - last >= timeoutMs) {
      onIdle()
      return
    }

    if (!last) setLastActivity()

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, onActivity, { passive: true })
    })
    document.addEventListener('visibilitychange', onVisibilityChange)
    scheduleLogout()
  }

  const stop = () => {
    clearTimer()
    ACTIVITY_EVENTS.forEach((event) => {
      window.removeEventListener(event, onActivity)
    })
    document.removeEventListener('visibilitychange', onVisibilityChange)
  }

  const stopWatcher = watch(
    () => authStore.isAuthenticated,
    (authenticated) => {
      stop()
      if (authenticated) {
        start()
      } else {
        clearLastActivity()
      }
    },
    { immediate: true }
  )

  onUnmounted(() => {
    stopWatcher()
    stop()
  })
}
